/**
 * Why a control the operator can see is not usable right now.
 *
 * A bare `disabled` button is silent: it says something is wrong without saying
 * what, and the two readings a user reaches for — "the app is broken" and "my
 * role is wrong" — are usually both false. The actions that take minutes are the
 * worst case, because the explanation ("a run is already in flight") lives in a
 * `RunStageLine` somewhere else in the layout, and nothing joins the two up.
 *
 * The wrapping is not decoration. A `disabled` element fires no pointer events,
 * so a `Tooltip` placed directly on one never opens; the surrounding `Box` is
 * what has a hover to listen to. Three hand-rolled copies of that idiom already
 * existed here (the lifecycle `RoleButton`, the sign-off button, the reports
 * Preview button) before this component collected them.
 *
 * Named for the *disabled* reason rather than a bare "reason" on purpose: in
 * this codebase a reason is the audit reason a mutation carries (`ReasonModal`,
 * `X-Reason`), and the collision would read as that.
 */

import { Box, type FloatingPosition, Tooltip } from "@mantine/core";
import type { ReactNode } from "react";

export function DisabledReasonTooltip({
  reason,
  position = "top",
  w = 260,
  children,
}: {
  /** Why the control is unusable, or `null`/`undefined` when it is usable. */
  reason: string | null | undefined;
  position?: FloatingPosition;
  /** Tooltip width; the copy is a sentence, so it always wraps. */
  w?: number;
  children: ReactNode;
}) {
  // Children pass through untouched when there is nothing to explain, rather
  // than always wrapping and suppressing the tooltip with Mantine's own
  // `disabled` prop. The `Box` is a real layout `div`, and emitting one around
  // every enabled control would change flex sizing at each call site for the
  // sake of a state most of them spend most of their time in.
  if (!reason) return <>{children}</>;
  return (
    <Tooltip label={reason} position={position} multiline w={w}>
      <Box>{children}</Box>
    </Tooltip>
  );
}
